import { useState, useEffect } from "react";
import { DatabaseBackup, Download, RotateCcw, Trash2, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { api, fmtDateTime, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KIND = {
  manual: { label: "Manuell", cls: "text-sky-400 border-sky-500/40" },
  auto: { label: "Automatisch", cls: "text-emerald-400 border-emerald-500/40" },
  pre_restore: { label: "Vor Wiederherstellung", cls: "text-amber-400 border-amber-500/40" },
};

const BackupManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backups");
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backups");
      toast.success("Sicherungskopie erstellt");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const doRestore = async () => {
    const b = restoreTarget;
    setRestoreTarget(null);
    if (!b) return;
    setBusy(b.id);
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`);
      toast.success(`Wiederhergestellt · ${data.counts?.courses ?? 0} Kurse, ${data.counts?.bookings ?? 0} Buchungen`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    const b = deleteTarget;
    setDeleteTarget(null);
    if (!b) return;
    setBusy(b.id);
    try {
      await api.delete(`/admin/backups/${b.id}`);
      toast.success("Sicherung gelöscht");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const download = async (b) => {
    setBusy(b.id);
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gca-backup-${(b.created_at || "").slice(0, 19).replace(/:/g, "-")}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="backup-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white flex items-center gap-3">
          <DatabaseBackup size={22} className="text-[#ff0033]" /> Sicherung &amp; Wiederherstellung
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="backup-refresh" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2.5 font-mono-lab text-xs uppercase tracking-widest disabled:opacity-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
          </button>
          <button onClick={createBackup} disabled={creating} data-testid="backup-create-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029] transition-colors disabled:opacity-50">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <DatabaseBackup size={14} />} Sicherungskopie jetzt erstellen
          </button>
        </div>
      </div>

      <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 mb-6 flex items-start gap-3" data-testid="backup-info">
        <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
        <div className="text-sm text-neutral-300 space-y-1">
          <p>Eine Sicherung umfasst <span className="text-white">alle Geschäftsdaten</span>: Kurse, Termine, Buchungen, Rechnungen, Kunden, Kategorien, Website-Inhalte, Gutscheine, TrackMan-Buchungen und News.</p>
          <p><span className="text-white">Automatisch:</span> Es wird täglich eine Sicherung erstellt (die letzten 14 werden aufbewahrt). Du kannst jederzeit zusätzlich manuell sichern – am besten vor jedem Deployment.</p>
          <p><span className="text-white">Wiederherstellen</span> setzt alle Daten auf den gewählten Stand zurück. Vorher wird automatisch eine Sicherung des aktuellen Zustands angelegt, damit auch das rückgängig gemacht werden kann.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 font-mono-lab text-sm py-10 text-center">Lädt…</div>
      ) : list.length === 0 ? (
        <div className="text-neutral-500 font-mono-lab text-sm py-10 text-center" data-testid="backup-empty">Noch keine Sicherungen vorhanden.</div>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest">
                <th className="text-left p-3">Datum &amp; Uhrzeit</th>
                <th className="text-left p-3">Typ</th>
                <th className="text-left p-3">Umfang</th>
                <th className="text-right p-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => {
                const k = KIND[b.kind] || { label: b.kind, cls: "text-neutral-400 border-white/20" };
                return (
                  <tr key={b.id} className="border-b border-white/5 align-top" data-testid={`backup-row-${b.id}`}>
                    <td className="p-3 text-white whitespace-nowrap">{fmtDateTime(b.created_at, "de")}</td>
                    <td className="p-3">
                      <span className={`inline-block font-mono-lab text-[10px] uppercase tracking-wide px-2 py-1 border ${k.cls}`}>{k.label}</span>
                      {b.label && <span className="block text-[11px] text-neutral-500 mt-1">{b.label}</span>}
                    </td>
                    <td className="p-3 text-neutral-400">
                      <span className="text-white">{b.total_docs}</span> Datensätze
                      <span className="block text-[11px] text-neutral-500">{b.counts?.courses ?? 0} Kurse · {b.counts?.bookings ?? 0} Buchungen · {b.counts?.invoices ?? 0} Rechnungen</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => setRestoreTarget(b)} disabled={busy === b.id} data-testid={`backup-restore-${b.id}`} className="inline-flex items-center gap-1 border border-amber-500/40 text-amber-400 font-mono-lab text-[10px] uppercase tracking-widest px-2.5 py-1.5 hover:bg-amber-500/10 disabled:opacity-40 transition-colors">
                          <RotateCcw size={12} /> Wiederherstellen
                        </button>
                        <button onClick={() => download(b)} disabled={busy === b.id} data-testid={`backup-download-${b.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-300 font-mono-lab text-[10px] uppercase tracking-widest px-2.5 py-1.5 hover:text-white disabled:opacity-40 transition-colors">
                          <Download size={12} /> Herunterladen
                        </button>
                        <button onClick={() => setDeleteTarget(b)} disabled={busy === b.id} data-testid={`backup-delete-${b.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-400 font-mono-lab text-[10px] uppercase tracking-widest px-2.5 py-1.5 hover:text-[#ff0033] disabled:opacity-40 transition-colors">
                          <Trash2 size={12} /> Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="backup-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Diesen Stand wiederherstellen?</AlertDialogTitle>
            <AlertDialogDescription>
              {restoreTarget && (
                <>Alle aktuellen Daten werden durch die Sicherung vom <span className="text-white">{fmtDateTime(restoreTarget.created_at, "de")}</span> ersetzt ({restoreTarget.total_docs} Datensätze). Der aktuelle Zustand wird vorher automatisch gesichert, damit du auch das rückgängig machen kannst.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-restore-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} data-testid="backup-restore-confirm" className="bg-amber-600 hover:bg-amber-700">Ja, wiederherstellen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="backup-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sicherung löschen?</AlertDialogTitle>
            <AlertDialogDescription>Diese Sicherung wird dauerhaft entfernt. Andere Sicherungen bleiben erhalten.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="backup-delete-confirm" className="bg-[#ff0033] hover:bg-[#cc0029]">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupManager;
